import { COLOR_MATCH, hueDistance } from '../constants';
import type { Camera } from '../engine/Camera';
import type { SkinPattern } from '../entities/Snake';
import { sampleSpline } from '../utils/spline';
import { clamp, type Vec2 } from '../utils/math';
import { drawPatternOverlay, hashSessionId } from './patternOverlays';
import {
  serverPlayers,
  serverFood,
  type ServerPlayer,
} from '../../net/serverWorld';

/**
 * Phase-2-Full Session 4 — renders the server-authoritative world
 * onto the canvas.
 *
 * Replaces the local SnakeRenderer + FoodRenderer pair for live-mode
 * gameplay. Distinct from RemoteSnakeRenderer (Phase 2 Lite) because:
 *
 *   - Data source is `serverWorld.serverPlayers` (driven by Colyseus
 *     state patches at 20 Hz), not Phase-2-Lite's snake_state map
 *     (driven by peer broadcast at 10 Hz).
 *   - Renders the LOCAL player's own snake too, from the server's
 *     view of it — input lag is visible until Session 5 wires
 *     client-side prediction.
 *   - Includes food rendering — server.food is the canon.
 *
 * Interpolation strategy mirrors RemoteSnakeRenderer: render with a
 * fixed 60 ms delay (the inter-patch period), giving us a prev/curr
 * window over which alpha rides 0→1. Slightly tighter delay than the
 * 100 ms used for the peer relay because Colyseus patches are more
 * regular than 10 Hz peer broadcasts.
 *
 * Lost-visual-richness note: this renderer deliberately skips the
 * client-only effects (tongue, chomp, digesting orbs, leader aura)
 * that the legacy SnakeRenderer wove together from local Snake
 * state. Those depend on per-eat events the server doesn't yet
 * broadcast at fine enough granularity. Session 4 trades visual
 * polish for architectural correctness; reintroducing those visuals
 * is a Session-5-or-later polish pass.
 */

/** Render delay = 2× server patch interval (50 ms × 2 = 100 ms) so
 *  prev+curr always bracket renderTime under ±25 ms patch-arrival
 *  jitter. The previous 60ms was BELOW the patch interval — the
 *  interpolator ran out of forward-data within ~10-40 ms of each
 *  patch and the snake rendered the SAME interpolated position
 *  frame after frame, producing the user-reported "stuck for a
 *  fraction of a second then jumps" stutter.
 *
 *  100ms here matches the cadence RemoteSnakeRenderer already uses
 *  (see its RENDER_DELAY_MS = 100 with the same bracketing comment).
 *  Costs ~40 ms of additional snake-to-pixel latency, unnoticeable
 *  next to the network RTT. */
const RENDER_DELAY_MS = 100;
const SAMPLES_PER_SEGMENT = 4;
const LOD_RADIUS_PX = 6;
const FADE_IN_MS = 200;

export class LiveWorldRenderer {
  private readonly pts: Vec2[] = [];
  private readonly samples: Vec2[] = [];
  private readonly xs: number[] = [];
  private readonly ys: number[] = [];

  // ── Radial-gradient cache ───────────────────────────────────────
  //
  // The stutter audit identified per-frame CanvasGradient allocation
  // as a primary GC trigger: thousands of gradients/frame across
  // every snake body sample and every food pellet were producing
  // tens of MB/s of garbage. V8 young-gen GC then stalled the main
  // thread for 80-250 ms — exactly the user-reported "snake stops
  // for a fraction of a second and resumes" symptom.
  //
  // Cache keys quantize radius to 0.5 px and lightness to 2 % so
  // values repeat across samples and frames at ~95 % hit rate.
  // Built at origin (0,0,0,0,0,r) so the same gradient object can
  // be reused at any (sx, sy) via ctx.translate.
  //
  // Bound: cleared when it grows past 2048 entries (rare — that's
  // ~700 unique hues × 3 radii × 1 lightness combo, far beyond the
  // realistic working set of <300 entries).
  //
  // Invalidation: if the ctx identity changes (canvas re-created on
  // a fresh game session) we drop the whole cache.
  private gradCache = new Map<string, CanvasGradient>();
  private gradCacheCtx: CanvasRenderingContext2D | null = null;

  private getCachedGradient(
    ctx: CanvasRenderingContext2D,
    key: string,
    radius: number,
    stops: Array<[number, string]>,
  ): CanvasGradient {
    if (ctx !== this.gradCacheCtx) {
      this.gradCache.clear();
      this.gradCacheCtx = ctx;
    }
    let g = this.gradCache.get(key);
    if (!g) {
      if (this.gradCache.size > 2048) this.gradCache.clear();
      g = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
      for (let i = 0; i < stops.length; i++) {
        g.addColorStop(stops[i]![0], stops[i]![1]);
      }
      this.gradCache.set(key, g);
    }
    return g;
  }

  /**
   * Draw the entire authoritative world: food first, snakes on top.
   * Food → snakes ordering keeps pellets visually behind body discs.
   */
  drawAll(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    time: number,
    playerHue: number,
    excludeId: string | null = null,
  ): void {
    this.drawFood(ctx, camera, time, playerHue);
    const renderTime = Date.now() - RENDER_DELAY_MS;
    for (const snake of serverPlayers.values()) {
      if (!snake.alive) continue;
      // Session 5: skip the local player — the engine renders them
      // via the prediction path with the full SnakeRenderer richness.
      if (excludeId !== null && snake.id === excludeId) continue;
      this.drawSnake(ctx, camera, snake, renderTime, time);
    }
  }

  // ── Food pass ──────────────────────────────────────────────────

  private drawFood(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    time: number,
    playerHue: number,
  ): void {
    const bounds = camera.getVisibleBounds();
    const z = camera.zoom;
    const cx = camera.viewportW * 0.5 + camera.shakeX;
    const cy = camera.viewportH * 0.5 + camera.shakeY;
    const cax = camera.position.x;
    const cay = camera.position.y;

    // Single pass — match-pulse aura UNDER the main disc, drawn here
    // by layering shadows. We don't run the two-pass aura that the
    // legacy renderer does because most pellets are off-color for
    // any given player; the aura cost was high for low value.
    ctx.save();
    ctx.shadowBlur = 14;
    for (let i = 0; i < serverFood.length; i++) {
      const p = serverFood[i]!;
      if (p.x + p.radius < bounds.minX || p.x - p.radius > bounds.maxX) continue;
      if (p.y + p.radius < bounds.minY || p.y - p.radius > bounds.maxY) continue;
      const r = p.radius * z;
      if (r < 1.2) continue;
      const sx = (p.x - cax) * z + cx;
      const sy = (p.y - cay) * z + cy;
      const pulse = 1 + 0.15 * Math.sin(time * 2.4 + p.phase);
      const rr = r * pulse;
      // Boost pulse + slight outer glow for color-match pellets so
      // the player can still spot their food at a glance.
      const isMatch = hueDistance(p.hue, playerHue) <= COLOR_MATCH.PERFECT_DEGREES;
      if (isMatch) {
        ctx.shadowColor = `hsl(${p.hue} 100% 70%)`;
        ctx.shadowBlur = 18;
      } else {
        ctx.shadowColor = `hsl(${p.hue} 100% 65%)`;
        ctx.shadowBlur = 8;
      }
      // Cached gradient at origin — reused across food pellets that
      // share the same hue + quantized radius (very common).
      const rQ = Math.round(rr * 2) / 2;
      const hQ = Math.round(p.hue);
      const key = `f|${hQ}|${rQ}`;
      const g = this.getCachedGradient(ctx, key, rQ, [
        [0,   `hsl(${hQ} 100% 88%)`],
        [0.6, `hsl(${hQ} 100% 58%)`],
        [1,   `hsla(${hQ} 100% 38% / 0)`],
      ]);
      // Gradient was built at origin — translate the path, not the
      // coords. Save/restore is cheap (~0.5 μs) vs createRadialGradient
      // + 3× addColorStop (~10-30 μs each), so even per-pellet this
      // is a net win.
      ctx.save();
      ctx.translate(sx, sy);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, rr, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    ctx.restore();
  }

  // ── Snake pass ─────────────────────────────────────────────────

  private drawSnake(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    p: ServerPlayer,
    renderTime: number,
    time: number,
  ): void {
    // Interpolate state. prevReceivedAt === 0 means first patch —
    // render at curr directly, no interpolation, fade in.
    let alpha: number;
    let hx: number, hy: number, heading: number, radius: number;
    let useCurrOnly: boolean;
    if (p.prevReceivedAt === 0) {
      alpha = 1;
      hx = p.headX; hy = p.headY; heading = p.heading; radius = p.radius;
      useCurrOnly = true;
    } else {
      const span = p.receivedAt - p.prevReceivedAt;
      alpha = span > 0
        ? clamp((renderTime - p.prevReceivedAt) / span, 0, 1)
        : 1;
      hx = lerp(p.prevHeadX, p.headX, alpha);
      hy = lerp(p.prevHeadY, p.headY, alpha);
      heading = angularLerp(p.prevHeading, p.heading, alpha);
      radius = lerp(p.prevRadius, p.radius, alpha);
      useCurrOnly = false;
    }

    // Build interpolated segment chain — fall back to curr-only if
    // prev/curr have different lengths (length changes happen when a
    // snake's mass crosses a segment-count threshold).
    const pts = this.pts;
    pts.length = 0;
    const curr = p.segments;
    const prev = p.prevSegments;
    if (useCurrOnly || curr.length !== prev.length) {
      for (let i = 0; i < curr.length; i += 2) {
        pts.push({ x: curr[i]!, y: curr[i + 1]! });
      }
    } else {
      for (let i = 0; i < curr.length; i += 2) {
        pts.push({
          x: lerp(prev[i]!, curr[i]!, alpha),
          y: lerp(prev[i + 1]!, curr[i + 1]!, alpha),
        });
      }
    }
    if (pts.length < 1) return;
    pts.unshift({ x: hx, y: hy });

    // Frustum cull.
    const bounds = camera.getVisibleBounds();
    const pad = radius * 1.8;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = 0; i < pts.length; i++) {
      const q = pts[i]!;
      if (q.x < minX) minX = q.x;
      if (q.y < minY) minY = q.y;
      if (q.x > maxX) maxX = q.x;
      if (q.y > maxY) maxY = q.y;
    }
    if (maxX + pad < bounds.minX || minX - pad > bounds.maxX) return;
    if (maxY + pad < bounds.minY || minY - pad > bounds.maxY) return;

    // Sample + project.
    const zoom = camera.zoom;
    const headRadPx = radius * zoom;
    const lod = headRadPx < LOD_RADIUS_PX;
    const density = lod ? 1 : SAMPLES_PER_SEGMENT;
    const samples = sampleSpline(pts, density, this.samples);
    const cx = camera.viewportW * 0.5 + camera.shakeX;
    const cy = camera.viewportH * 0.5 + camera.shakeY;
    const cax = camera.position.x;
    const cay = camera.position.y;
    const n = samples.length;
    const xs = this.xs;
    const ys = this.ys;
    while (xs.length < n) { xs.push(0); ys.push(0); }
    for (let i = 0; i < n; i++) {
      const s = samples[i]!;
      xs[i] = (s.x - cax) * zoom + cx;
      ys[i] = (s.y - cay) * zoom + cy;
    }

    // Fade in + invincibility flicker (both purely visual).
    const firstSeen = p.prevReceivedAt === 0 ? p.receivedAt : p.prevReceivedAt;
    const ageMs = Date.now() - firstSeen;
    const fadeAlpha = clamp(ageMs / FADE_IN_MS, 0, 1);
    const isInvincible = Date.now() < p.invincibleUntil;
    const invAlpha = isInvincible ? 0.45 + 0.55 * Math.abs(Math.sin(time * 12)) : 1;
    const finalAlpha = fadeAlpha * invAlpha;
    const needsAlpha = finalAlpha < 0.999;
    if (needsAlpha) {
      ctx.save();
      ctx.globalAlpha = finalAlpha;
    }

    if (lod) {
      this.drawLOD(ctx, p.hue, xs, ys, n, radius);
    } else {
      this.drawBody(ctx, p.hue, xs, ys, n, radius, p.boosting, time);
      // Pattern overlay on top of the base body — same code path the
      // local SnakeRenderer uses, so remote snakes render with their
      // glitch/plasma/geometric/bio skin instead of looking like a
      // flat default tube.
      if (p.pattern) {
        drawPatternOverlay(ctx, p.pattern as SkinPattern, {
          hue: p.hue,
          idSeed: hashSessionId(p.id),
          widthAt: (u) => Math.max(1.2, radius * (1 - 0.55 * Math.pow(u, 1.2))),
        }, xs, ys, n, time);
      }
      this.drawHead(ctx, p.hue, xs[0]!, ys[0]!, headRadPx, heading);
      if (p.name) {
        // Newcomer = real human within their first 60 s of session.
        // Renders a small "✦ NEW" badge under the name so other
        // players have a social cue to leave them alone (the bots
        // already do via repelFromNewcomers in ServerSnakeAI).
        const isNewcomer = !p.isBot && p.joinedAt > 0 && (Date.now() - p.joinedAt) < 60_000;
        this.drawNameLabel(ctx, p.name, p.hue, xs[0]!, ys[0]!, headRadPx, isNewcomer);
      }
    }

    if (needsAlpha) ctx.restore();
  }

  private drawLOD(
    ctx: CanvasRenderingContext2D,
    hue: number,
    xs: number[],
    ys: number[],
    n: number,
    radius: number,
  ): void {
    ctx.beginPath();
    ctx.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = `hsl(${hue} 100% 60%)`;
    ctx.lineWidth = Math.max(1.5, radius * 1.2);
    ctx.stroke();
  }

  /**
   * Body: outer glow + radial-gradient discs + optional boost rim.
   * Same neon-tube aesthetic as the legacy SnakeRenderer, simplified.
   */
  private drawBody(
    ctx: CanvasRenderingContext2D,
    hue: number,
    xs: number[],
    ys: number[],
    n: number,
    radius: number,
    boosting: boolean,
    time: number,
  ): void {
    const taper = 0.45;

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = `hsl(${hue} 100% 65%)`;
    ctx.shadowBlur = 20;
    ctx.strokeStyle = `hsla(${hue} 100% 65% / 0.42)`;
    ctx.lineWidth = radius * 2.4;
    ctx.beginPath();
    ctx.moveTo(xs[0]!, ys[0]!);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
    ctx.stroke();
    ctx.restore();

    const phaseSpeed = 0.5 * time * Math.PI * 2;
    for (let i = 0; i < n; i++) {
      const u = i / (n - 1);
      const r = Math.max(1.2, radius * (1 - (1 - taper) * Math.pow(u, 1.2)));
      const phase = u * 5 * Math.PI * 2 - phaseSpeed;
      const tNorm = 0.5 + 0.5 * Math.sin(phase);
      const innerLight = clamp(54 + 28 * 0.9 + 28 * 0.5 * tNorm, 4, 96);
      const outerLight = clamp(54 - 28 * 0.6 - 16 - 6 * u, 2, 80);
      const sx = xs[i]!;
      const sy = ys[i]!;
      // Quantize cache key components: radius to 0.5 px, lightness to
      // 2 %. With ~4 samples per segment and slowly-varying lightness
      // along the body, neighboring samples almost always hit the
      // same cache key — typical hit rate >95 %.
      const rQ = Math.round(r * 2) / 2;
      const inQ = Math.round(innerLight / 2) * 2;
      const outQ = Math.round(outerLight / 2) * 2;
      const midQ = Math.round(Math.max(outQ, inQ * 0.55) / 2) * 2;
      const hQ = Math.round(hue);
      const key = `b|${hQ}|${rQ}|${inQ}|${midQ}|${outQ}`;
      const g = this.getCachedGradient(ctx, key, rQ, [
        [0,    `hsl(${hQ} 100% ${inQ}%)`],
        [0.55, `hsl(${hQ} 100% ${midQ}%)`],
        [1,    `hsl(${hQ} 100% ${outQ}%)`],
      ]);
      ctx.save();
      ctx.translate(sx, sy);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    if (boosting) {
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = `hsla(${hue} 100% 78% / 0.85)`;
      ctx.shadowColor = `hsl(${hue} 100% 70%)`;
      ctx.shadowBlur = 12;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(xs[0]!, ys[0]!);
      for (let i = 1; i < n; i++) ctx.lineTo(xs[i]!, ys[i]!);
      ctx.stroke();
      ctx.restore();
    }
  }

  private drawHead(
    ctx: CanvasRenderingContext2D,
    hue: number,
    sx: number,
    sy: number,
    rPx: number,
    heading: number,
  ): void {
    // Heads share gradients across snakes at the same zoom × radius.
    const rQ = Math.round(rPx * 2) / 2;
    const hQ = Math.round(hue);
    const headKey = `h|${hQ}|${rQ}`;
    const g = this.getCachedGradient(ctx, headKey, rQ, [
      [0,   `hsl(${hQ} 100% 78%)`],
      [0.6, `hsl(${hQ} 100% 58%)`],
      [1,   `hsl(${hQ} 100% 32%)`],
    ]);
    ctx.save();
    ctx.translate(sx, sy);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(0, 0, rPx, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    const eyeR = Math.max(1.4, rPx * 0.22);
    const fx = Math.cos(heading);
    const fy = Math.sin(heading);
    const ex = sx + fx * rPx * 0.35;
    const ey = sy + fy * rPx * 0.35;
    ctx.fillStyle = `hsl(${hue} 60% 14%)`;
    ctx.beginPath();
    ctx.arc(ex, ey, eyeR, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * Name label above the head. Uniform style — no distinction between
   * humans and bots. The mystery factor is the gameplay design: the
   * player should chase the snake without knowing whether the kill
   * would land them on a real-player or a bot leaderboard. A
   * lit-up "this one is real!" badge would defuse that tension.
   */
  private drawNameLabel(
    ctx: CanvasRenderingContext2D,
    name: string,
    hue: number,
    sx: number,
    sy: number,
    rPx: number,
    isNewcomer: boolean = false,
  ): void {
    if (rPx < 8) return;
    const labelY = sy - rPx - 14;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '600 11px "Space Grotesk", system-ui, sans-serif';
    ctx.strokeStyle = 'rgba(0,0,0,0.55)';
    ctx.lineWidth = 3;
    ctx.strokeText(name, sx, labelY);
    ctx.fillStyle = `hsl(${hue} 100% 78%)`;
    ctx.shadowColor = `hsla(${hue} 100% 60% / 0.6)`;
    ctx.shadowBlur = 6;
    ctx.fillText(name, sx, labelY);

    // ── Newcomer badge ───────────────────────────────────────────
    // Small "✦ NEW" pill below the name. Cyan glow so it reads as
    // a friendly "welcome them" cue, not a threat warning. Other
    // humans see this and (hopefully) leave the newcomer alone for
    // their first 60 s — the bots are already steering away from
    // them via repelFromNewcomers.
    if (isNewcomer) {
      const badgeY = sy - rPx - 28;
      ctx.shadowBlur = 8;
      ctx.shadowColor = 'rgba(94,216,255,0.85)';
      ctx.font = '700 9px "Space Grotesk", system-ui, sans-serif';
      ctx.strokeStyle = 'rgba(0,0,0,0.65)';
      ctx.lineWidth = 3;
      ctx.strokeText('✦ NEW', sx, badgeY);
      ctx.fillStyle = 'hsl(190 100% 80%)';
      ctx.fillText('✦ NEW', sx, badgeY);
    }
    ctx.restore();
  }
}

// ─── helpers ───────────────────────────────────────────────────────

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function angularLerp(a: number, b: number, t: number): number {
  const TAU = Math.PI * 2;
  let d = ((b - a) % TAU + TAU) % TAU;
  if (d > Math.PI) d -= TAU;
  return a + d * t;
}
